import React, { startTransition, useMemo } from 'react'
import { useEnv } from '@praxis/component-runtime-env'
import { makeStyles } from '@material-ui/core/styles'
import InputAdornment from '@material-ui/core/InputAdornment'
import TextField from '@material-ui/core/TextField'
import ImageNA from '../../images/ImageNA.svg'
import SearchIcon from '@material-ui/icons/Search'
import IconButton from '@material-ui/core/IconButton'
import Switch from '@material-ui/core/Switch'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import Checkbox from '@material-ui/core/Checkbox'
import Button from '@material-ui/core/Button'
import TablePagination from '@material-ui/core/TablePagination'
import Container from '@material-ui/core/Container'
import CircularProgress from '@material-ui/core/CircularProgress'
import Tabs from '@material-ui/core/Tabs'
import Tab from '@material-ui/core/Tab'
import AppBar from '@material-ui/core/AppBar'
import Box from '@material-ui/core/Box'
import Chip from '@material-ui/core/Chip'
import PaginationActions from '../TcinInfo/PaginationActions'
import ProjectTable from './ProjectTable'
import { formatDate } from '../../helpers/dateHelper'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormControl from '@mui/material/FormControl'
import { Link } from 'react-router-dom'
import ClearIcon from '@mui/icons-material/Clear'
import ListAltIcon from '@mui/icons-material/ListAlt'
import InsightsIcon from '@mui/icons-material/Insights'
import {
  getAllProjectsAPI,
  setIsMyProjects,
  setIsIncludeArchived,
  setProjectSearchText,
  setSearchType,
} from '../../store/Dashboard/actionCreator'
import { useDispatch, useSelector } from 'react-redux'
import apiConfig from '../../apiConfig'
import { withAuth } from '@praxis/component-auth'
import { producerADGroup } from '../../constants/common'
import { isSafari } from '../../helpers/filter'
import { setBreadcrumbs } from '../../store/Layout/actionCreator'
import { getSubCompanyList } from '../Workflow/constants'
import { parseDateOnly } from './utils'

const useStyles = makeStyles((theme) => ({
  root: {
    margin: '10px 5px',
  },
  searchContainer: {
    position: 'relative',
    height: 'auto',
    padding: 0,
    margin: '0 0 0 auto !important',
    width: 'auto !important',
  },
  topMenu: {
    marginTop: isSafari() ? '57px' : '16px',
    display: 'flex',
    alignItems: 'center',
    gap: 56,
  },
  tableMenu: {
    padding: 25,
  },
  projectHeading: {
    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: '400',
    fontSize: '18px',
    lineHeight: '21px',
    color: '#333333',
  },
  checkedSwitch: {
    color: '#366CD9 !important',
  },
  paginationToolbar: {
    marginLeft: 'auto',
  },
  tableActionMenu: {
    display: 'flex',
    alignItems: 'center',
    gap: 56,
  },
  inputFindProject: {
    transform: 'translate(14px, 13px) scale(1)',
  },
  inputProps: {
    height: 40,
    padding: '0 8px',
  },
  inputWidth: {
    width: 423,
  },
  createProjButton: {
    marginLeft: 'auto',
    height: 36,
    width: 150,
    textDecoration: 'none',
  },
  tabLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  switch_track: {
    backgroundColor: '#B6B6B6',
  },
  switch_base: {
    color: '#B6B6B6',
    '&.Mui-checked': {
      color: '#366CD9',
    },
    '&.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#366CD9',
    },
  },
  switch_primary: {
    '&.Mui-checked': {
      color: '#366CD9',
    },
    '&.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#366CD9',
    },
  },
}))

const ProjectList = (props) => {
  const { company = '', memberOf = [], lanId = '' } = props
  const env = useEnv()
  const abortRef = React.useRef(null)
  const { restrictedUsers = [] } = env
  const pbSubCompanyList = getSubCompanyList(company)
  const [isMyProjectsFlag, setIsMyProjectsFlag] = React.useState(false)
  const [order, setOrder] = React.useState('desc')
  const [orderBy, setOrderBy] = React.useState('created_date')
  const [projects, setProjects] = React.useState([])
  const [page, setPage] = React.useState(0)
  const [rowsPerPage, setRowsPerPage] = React.useState(50)
  const [activeTab, setActiveTab] = React.useState(0)
  const isMyProjectsFlagRef = React.useRef(isMyProjectsFlag) // Ref to persist the latest value
  const hasMounted = React.useRef(false) // Ref to track if the component has mounted
  const classes = useStyles()
  const memberOfGroups = Array.isArray(props.memberOf) ? props.memberOf : []
  const isRestrictedUser = restrictedUsers.some((group) =>
    [...memberOfGroups].includes(group)
  )
  const storeDispatch = useDispatch()
  const {
    isIncludedArchived,
    isMyProjects,
    searchQuery,
    total_count,
    searchType,
    projectDetailsLoading,
  } = useSelector(
    ({
      dashboardReducer: {
        isIncludedArchived = false,
        isMyProjects = false,
        searchQuery = '',
        total_count = 50,
        searchType = 'PROJECT_NAME',
        projectDetailsLoading = false,
      },
    }) => {
      return {
        isIncludedArchived,
        isMyProjects,
        searchQuery,
        total_count,
        searchType,
        projectDetailsLoading,
      }
    }
  )

  const projectDetailsMap = useSelector(
    (state) => state.dashboardReducer?.projectDetails || {}
  )
  const today = useMemo(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), now.getDate())
  }, [])
  const normalize = (value) => String(value ?? '').trim()

  const fetchProjects = React.useCallback(
    (overrides = {}) => {
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller
      storeDispatch(
        getAllProjectsAPI(
          lanId,
          apiConfig.isPol ? pbSubCompanyList?.join(',') || company || '' : '',
          overrides.page ?? page,
          overrides.rowsPerPage ?? rowsPerPage,
          overrides.searchQuery ?? searchQuery,
          overrides.searchType ?? searchType,
          overrides.orderBy ?? orderBy,
          overrides.order ?? order,
          overrides.isMyProjects ?? isMyProjectsFlagRef.current,
          { signal: controller.signal }
        )
      )
    },
    [
      storeDispatch,
      lanId,
      pbSubCompanyList,
      company,
      page,
      rowsPerPage,
      searchQuery,
      searchType,
      orderBy,
      order,
    ]
  )

  React.useEffect(() => {
    return () => abortRef.current?.abort()
  }, [])

  const getProjectStatus = (project) => {
    const projectId = normalize(project.project_id)
    const detail = projectDetailsMap[projectId]
    const total = detail?.total_tcins ?? 0
    const done = detail?.total_completion ?? 0
    const percent = total > 0 ? Math.round((done / total) * 100) : 0
    const startDate = parseDateOnly(detail?.project_start_date)
    const endDate = parseDateOnly(detail?.project_end_date)
    const isCompleted = percent === 100
    const isDue =
      !isCompleted && !!endDate && today.getTime() > endDate.getTime()
    const isInProgress =
      !isCompleted &&
      !isDue &&
      !!startDate &&
      today.getTime() >= startDate.getTime() &&
      (!endDate || today.getTime() <= endDate.getTime())
    const isYetToStart = !isCompleted && !isDue && !isInProgress

    let statusLabel = 'In Progress'
    let statusColor = '#1976d2'
    if (isCompleted) {
      statusLabel = 'Completed'
      statusColor = '#2e7d32'
    } else if (isYetToStart) {
      statusLabel = 'Yet to Start'
      statusColor = '#616161'
    } else if (isDue) {
      statusLabel = 'Overdue'
      statusColor = '#d32f2f'
    }

    return {
      done: done || 0,
      total: total || 0,
      percent: percent || 0,
      isCompleted,
      isDue,
      isInProgress,
      statusLabel,
      statusColor,
      startDate,
      endDate,
    }
  }

  const getProgressBarColor = (percent, isDue) => {
    if (isDue) {
      return 'linear-gradient(90deg, #CC0000 0%, #e84040 50%, #f08080 100%)'
    }
    if (percent >= 75) {
      return 'linear-gradient(90deg, #10b981 0%, #34d399 50%, #6ee7b7 100%)'
    }
    return 'linear-gradient(90deg, #f59e0b 0%, #fbbf24 50%, #fde68a 100%)'
  }

  const getBadgeColor = (percent, isDue) => {
    if (isDue) {
      return 'linear-gradient(135deg, #CC0000 0%, #e84040 100%)'
    }
    if (percent >= 75) {
      return 'linear-gradient(135deg, #10b981 0%, #34d399 100%)'
    }
    return 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)'
  }

  React.useEffect(() => {
    if (!hasMounted.current) {
      startTransition(() => {
        setIsMyProjectsFlag(isMyProjects)
        isMyProjectsFlagRef.current = isMyProjects
        hasMounted.current = true
      })
    }
  }, [isMyProjects])

  React.useEffect(() => {
    storeDispatch(setBreadcrumbs(props.customPath, props.path))
    return () => {
      storeDispatch(setIsMyProjects(isMyProjectsFlagRef.current))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (!hasMounted.current) {
      return
    }
    fetchProjects({
      lanId,
      company: apiConfig.isPol
        ? pbSubCompanyList?.join(',') || company || ''
        : '',
      page,
      rowsPerPage,
      searchQuery,
      searchType,
      orderBy,
      order,
      isMyProjects: isMyProjectsFlagRef.current,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isMyProjectsFlagRef.current,
    isIncludedArchived,
    page,
    rowsPerPage,
    orderBy,
    order,
  ])

  const tableData = useSelector((state) => state.dashboardReducer.projects)

  React.useEffect(() => {
    setProjects(tableData)
  }, [tableData])

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' || event.key === 'Tab') {
      fetchProjects({
        lanId,
        company: apiConfig.isPol
          ? pbSubCompanyList?.join(',') || company || ''
          : '',
        page,
        rowsPerPage,
        searchQuery,
        searchType,
        orderBy,
        order,
        isMyProjects: isMyProjectsFlagRef.current,
      })
    }
  }
  const handleIsIncludeArchived = (event) => {
    storeDispatch(setIsIncludeArchived(event.target.checked))
  }

  const handleProjectChange = (event) => {
    startTransition(() => {
      setPage(0)
      setIsMyProjectsFlag(event.target.checked)
      isMyProjectsFlagRef.current = event.target.checked
    })
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event) => {
    startTransition(() => {
      setRowsPerPage(parseInt(event.target.value, 10))
      setPage(0)
    })
  }

  const onChangeSearch = (e) => {
    startTransition(() => {
      setPage(0)
      storeDispatch(setProjectSearchText(e.target.value))
    })
  }

  const clearSearch = () => {
    startTransition(() => {
      storeDispatch(setProjectSearchText(''))
      storeDispatch(setSearchType('PROJECT_NAME'))
      fetchProjects({
        lanId,
        company: apiConfig.isPol
          ? pbSubCompanyList?.join(',') || company || ''
          : '',
        page,
        rowsPerPage,
        searchQuery,
        searchType,
        orderBy,
        order,
        isMyProjects: isMyProjectsFlagRef.current,
      })
    })
  }

  const renderTotalPages = () => {
    return (
      <div className={classes.tableActionMenu}>
        <Typography variant="h6" className={classes.projectHeading}>
          Showing {tableData.length} Projects
        </Typography>
        <TablePagination
          classes={{
            root: classes.paginationToolbar,
          }}
          component="div"
          rowsPerPageOptions={[
            50,
            100,
            200,
            { label: 'All', value: total_count },
          ]}
          colSpan={3}
          count={total_count}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={PaginationActions}
        />
      </div>
    )
  }

  const renderSearchBy = () => {
    return (
      <FormControl component="fieldset">
        <RadioGroup
          row
          name="ViewType"
          onChange={(e) => storeDispatch(setSearchType(e.target.value))}
          value={searchType}
        >
          <FormControlLabel
            control={<Typography variant="body1">Search By:</Typography>}
            sx={{ color: 'black', paddingLeft: '10px' }}
          />
          <FormControlLabel
            value="PROJECT_NAME"
            control={<Radio />}
            label="Project Name"
            sx={{
              color: searchType === 'PROJECT_NAME' ? 'black' : 'gray',
            }}
          />
          <FormControlLabel
            value="PROJECT_ID"
            control={<Radio />}
            label="Project Id"
            sx={{ color: searchType === 'PROJECT_ID' ? 'black' : 'gray' }}
          />
        </RadioGroup>
      </FormControl>
    )
  }

  const renderSearchContainer = () => {
    return (
      <TextField
        label="Find Project"
        id="find-project"
        InputLabelProps={{
          classes: {
            outlined: classes.inputFindProject,
          },
        }}
        onChange={onChangeSearch}
        onKeyDown={handleKeyPress}
        value={searchQuery}
        InputProps={{
          classes: {
            inputAdornedEnd: classes.inputProps,
            adornedEnd: classes.inputWidth,
          },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton aria-label="find_project" edge="end">
                <SearchIcon />
              </IconButton>
              {searchQuery !== '' && (
                <IconButton
                  aria-label="find_project"
                  edge="end"
                  onClick={clearSearch}
                >
                  <ClearIcon />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
        variant="outlined"
      />
    )
  }

  const renderMyProjectsSwitch = () => {
    return (
      <Grid component="label" container alignItems="center" spacing={1}>
        <Grid item>All Projects</Grid>
        <Grid item>
          <Switch
            classes={{
              track: classes.switch_track,
              switchBase: classes.switch_base,
              colorPrimary: classes.switch_primary,
            }}
            checked={isMyProjectsFlag}
            onChange={handleProjectChange}
            name="allProjects"
            color="primary"
            disabled={apiConfig.isPol}
          />
        </Grid>
        <Grid item>My Projects</Grid>
      </Grid>
    )
  }

  const renderIncludeArchive = () => {
    return (
      <FormControlLabel
        control={
          <Checkbox
            checked={isIncludedArchived}
            classes={{
              checked: classes.checkedSwitch,
            }}
            onChange={handleIsIncludeArchived}
            name="isIncludeArchived"
            color="primary"
          />
        }
        label="Include Archived"
      />
    )
  }

  const renderCreateProject = () => {
    return (
      memberOf?.includes(producerADGroup.toUpperCase()) &&
      !apiConfig.isPol && (
        <Link to={'/dashboard/create'} className={classes.createProjButton}>
          <Button variant="contained" color="primary">
            Create Project
          </Button>
        </Link>
      )
    )
  }

  const renderMetricsView = () => {
    return projectDetailsLoading ? (
      <Box
        sx={{
          minHeight: 360,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress size={32} />
      </Box>
    ) : (
      <Box style={{ padding: '16px 24px' }}>
        {projects.map((project) => {
          const {
            done,
            total,
            percent,
            isDue,
            statusLabel,
            statusColor,
            startDate,
            endDate,
          } = getProjectStatus(project)

          return (
            <Box
              key={project.project_id}
              style={{
                padding: '16px 24px',
                marginBottom: 0,
                borderBottom: '1px solid #e0e0e0',
                backgroundColor: '#fff',
              }}
            >
              {/* Header row: name, dates, status, expand */}
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                }}
              >
                <Link
                  to={'/dashboard/' + project.project_id}
                  onClick={() => localStorage.setItem('projecttab', 'metrics')}
                  style={{
                    fontWeight: 600,
                    fontSize: 16,
                    color: '#333',
                    textDecoration: 'underline',
                  }}
                >
                  {project.project_name}
                </Link>
                <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Typography
                    variant="body2"
                    style={{ color: '#666', fontSize: 13 }}
                  >
                    {formatDate(startDate)}
                    {' \u2192 '}
                    {endDate ? formatDate(endDate) : '\u2014'}
                  </Typography>
                  <Chip
                    label={statusLabel}
                    size="small"
                    style={{
                      backgroundColor: statusColor,
                      color: 'white',
                      fontWeight: 600,
                      fontSize: 12,
                    }}
                  />
                </Box>
              </Box>

              {/* Progress row: badge + bar */}
              <Box
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 16,
                }}
              >
                {/* Circular percentage badge */}
                <Box
                  style={{
                    minWidth: 75,
                    height: 75,
                    borderRadius: '50%',
                    background: getBadgeColor(percent, isDue),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: isDue
                      ? '0 2px 10px rgba(204,0,0,0.4)'
                      : percent >= 75
                        ? '0 2px 10px rgba(16,185,129,0.45)'
                        : '0 2px 10px rgba(245,158,11,0.4)',
                  }}
                >
                  <Typography
                    style={{
                      fontSize: 16,
                      fontWeight: 800,
                      color: '#fff',
                      lineHeight: 1,
                    }}
                  >
                    {percent || 0}%
                  </Typography>
                </Box>

                {/* Bar section */}
                <Box style={{ flex: 1 }}>
                  <Box
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: 4,
                    }}
                  >
                    <Typography
                      variant="body2"
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: 'rgba(35,41,60,0.65)',
                      }}
                    >
                      TCIN Completion :{' '}
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 800,
                          color: 'rgba(35,41,60,0.85)',
                        }}
                      >
                        {`${done} / ${total} (${percent}%)`}
                      </span>
                    </Typography>
                  </Box>
                  <Box
                    style={{
                      width: '100%',
                      height: 12,
                      borderRadius: 6,
                      backgroundColor: 'rgba(35,41,60,0.07)',
                      overflow: 'hidden',
                      position: 'relative',
                    }}
                  >
                    <Box
                      style={{
                        width: `${percent}%`,
                        height: '100%',
                        borderRadius: 6,
                        background: getProgressBarColor(percent, isDue),
                        transition: 'width 0.7s cubic-bezier(.4,0,.2,1)',
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          )
        })}
      </Box>
    )
  }

  const renderProjectTable = () => {
    return (
      <ProjectTable
        page={page}
        rowsPerPage={rowsPerPage}
        searchText={searchQuery}
        isMyProjects={isMyProjectsFlag}
        projects={projects}
        orderBy={orderBy}
        order={order}
        orderHandler={(orderType = 'desc') => {
          setOrder(orderType)
        }}
        orderByHandler={(orderByValue = 'created_date') => {
          setOrderBy(orderByValue)
        }}
      />
    )
  }

  return (
    <div>
      <Container maxWidth={false} className={classes.topMenu}>
        <Grid container direction="column">
          <Grid item>{renderSearchBy()}</Grid>
          <Grid item>{renderSearchContainer()}</Grid>
        </Grid>
        <Grid
          container
          alignItems="center"
          justifyContent="flex-start"
          spacing={4}
        >
          {!apiConfig.isPol && <Grid item>{renderMyProjectsSwitch()}</Grid>}
          <Grid item>{renderIncludeArchive()}</Grid>
          <Grid item>{renderCreateProject()}</Grid>
        </Grid>
      </Container>
      <Container maxWidth={false} className={classes.tableMenu}>
        {renderTotalPages()}
      </Container>
      <AppBar
        position="static"
        style={{
          backgroundColor: 'white',
          color: 'black',
          boxShadow: 'none',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            label={
              <span className={classes.tabLabel}>
                <ListAltIcon fontSize="small" style={{ color: 'inherit' }} />
                Projects
              </span>
            }
          />
          {!isRestrictedUser && (
            <Tab
              label={
                <span className={classes.tabLabel}>
                  <InsightsIcon fontSize="small" style={{ color: 'inherit' }} />
                  Metrics
                </span>
              }
            />
          )}
        </Tabs>
      </AppBar>
      {activeTab === 0 && renderProjectTable()}
      {activeTab === 1 && !isRestrictedUser && renderMetricsView()}
    </div>
  )
}

const mapAuthToProps = (auth) => {
  return {
    accessToken: auth.session?.userInfo?.accessToken,
    company: auth.session?.userInfo?.company,
    memberOf: auth.session?.userInfo?.memberOf,
    lanId: auth.session?.userInfo?.lanId,
  }
}

export default withAuth(mapAuthToProps)(React.memo(ProjectList))
